import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <header><Navbar /></header>
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，无法找到该文章</p>
            <Link
              to="/"
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '继母的朋友们漫画资讯站',
    },
    publisher: {
      '@type': 'Organization',
      name: '继母的朋友们漫画资讯站',
    },
    inLanguage: 'zh-CN',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://stepmother-friends-manga.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <header>
        <Navbar />
      </header>
      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb items={getArticleBreadcrumb(article)} />
              <div className="mt-2">
                <div className="flex flex-wrap gap-2 mb-3">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-secondary-100 text-secondary-900 text-xs rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> 阅读时间 {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-auto max-h-[450px] object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-background-100 text-foreground-600 text-xs rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{related.publishDate}</span>
                        <span>{related.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}